import { z } from "zod";
import { AppliedScenarioMetricKeySchema } from "./applied-scenario-metrics.js";
import { ClassificationViewSchema } from "./classification.js";
import { ConfidenceLevelSchema } from "./simulations-enums.js";
import { ConfidenceBasisSchema, TrackRecordSourceSchema } from "./track-record.js";

// O CENÁRIO APLICADO: o registro de que um cenário simulado virou compromisso para uma
// janela real, e a apuração que compara previsto × realizado × contrafactual.
// Três decisões deste contrato que NÃO são cosméticas:
//  1. `windowEnd` NUNCA vem do cliente — é derivado consumindo expediente, e o `windowBasis`
//     declara COMO. Aceitá-lo do cliente faria a vigência e a rodada medirem janelas diferentes.
//  2. `deltaVsPredicted`/`deltaVsBaseline` são campos de RESPOSTA derivados na leitura,
//     nunca colunas — duas verdades capazes de divergir da primeira.
//  3. `withinTolerance` é "ficou na faixa DECLARADA NO ATO", não acurácia. A acurácia
//     depende de aderência ao plano, que é iteração seguinte.

/** Os QUATRO estados persistidos (FR-021). "Agendado"/"em execução" NÃO estão aqui: são fase. */
export const AppliedScenarioStateSchema = z.enum(["ACTIVE", "SETTLED", "SUPERSEDED", "CANCELLED"]);
export type AppliedScenarioState = z.infer<typeof AppliedScenarioStateSchema>;

/**
 * A FASE, derivada do relógio na leitura — nunca persistida e sem processo de fundo que a
 * transicione. Um estado que só existe porque o tempo passou não precisa de linha no banco; ter
 * uma exigiria um job para mantê-la verdadeira, e um job atrasado mostraria "em execução" numa
 * janela encerrada ontem.
 */
export const AppliedScenarioPhaseSchema = z.enum([
  "scheduled",
  "running",
  "awaiting_settlement",
  "settleable",
  "settled",
  "superseded",
  "cancelled",
]);
export type AppliedScenarioPhase = z.infer<typeof AppliedScenarioPhaseSchema>;

// ── ADERÊNCIA ao compromisso ───────────────────────────────────────────────────────────────────
// Mede se a operação seguiu o que foi combinado, em quatro dimensões independentes. NÃO se confunde
// com o `planAdherence` do OEE, que compara produção contra o ritmo do plano de UMA run:
// aqui o alvo é o COMPROMISSO congelado, e o número gateia a estatística de acurácia do gêmeo.

export const AdherenceDimensionKeySchema = z.enum(["sequence", "mix", "shifts", "machines"]);
export type AdherenceDimensionKey = z.infer<typeof AdherenceDimensionKeySchema>;

export const AdherenceVerdictSchema = z.enum(["ADHERENT", "AT_RISK", "BREACHED", "NOT_MEASURED"]);
export type AdherenceVerdict = z.infer<typeof AdherenceVerdictSchema>;

export const AdherenceSchema = z.object({
  /** O MÍNIMO das dimensões medidas. `null` = nenhuma era mensurável — jamais `0`. */
  index: z.number().finite().nullable(),
  verdict: AdherenceVerdictSchema,
  /** As quatro, cada uma `number | null`. `null` = NÃO MEDIDA, que não é zero. */
  dimensions: z.record(AdherenceDimensionKeySchema, z.number().finite().nullable()),
  /** A dimensão responsável pelo índice; nula quando o índice é nulo. */
  driverDimension: AdherenceDimensionKeySchema.nullable(),
  measuredAt: z.string(),
  /**
   * `true` = valor CONGELADO na apuração; `false` = derivado agora, muda no próximo tique.
   *
   * Viaja porque a mesma forma serve dois significados — a aderência de um compromisso vigente é
   * uma foto que muda, a de um apurado é história. Sem o campo, a tela não tem como parar de
   * prometer atualização num número que não vai mais se mexer.
   */
  frozen: z.boolean(),
});
export type Adherence = z.infer<typeof AdherenceSchema>;

/** `calendar` = fim derivado consumindo expediente; `wall_clock` = linha sem expediente declarado. */
export const AppliedScenarioWindowBasisSchema = z.enum(["calendar", "wall_clock"]);
export type AppliedScenarioWindowBasis = z.infer<typeof AppliedScenarioWindowBasisSchema>;

export const MetricToleranceSchema = z.object({
  metric: AppliedScenarioMetricKeySchema,
  /** Faixa aceitável em % sobre o previsto. Declarada ANTES, nunca na apuração. */
  tolerancePct: z.number().positive().max(1000),
});
export type MetricTolerance = z.infer<typeof MetricToleranceSchema>;

export const ApplyScenarioRequestSchema = z.object({
  simulationId: z.string().uuid(),
  /** Início declarado pelo gestor; nunca no passado. O FIM é derivado pelo servidor. */
  windowStart: z.string().datetime(),
  reason: z.string().min(1).max(500),
  tolerances: z.array(MetricToleranceSchema).default([]),
  /**
   * Limiar de aderência, em %. **Obrigatório**, e não opcional com default no servidor: um
   * limiar que aparece sozinho não foi declarado por ninguém, e o gate de acurácia perde a razão de
   * existir. Declará-lo DEPOIS de ver o resultado é o viés de seleção que a feature existe para
   * impedir. O formulário sugere 90; o servidor não sugere nada.
   */
  adherenceThresholdPct: z.number().min(0).max(100),
});
export type ApplyScenarioRequest = z.infer<typeof ApplyScenarioRequestSchema>;

export const CancelAppliedScenarioRequestSchema = z.object({
  reason: z.string().min(1).max(500),
});
export type CancelAppliedScenarioRequest = z.infer<typeof CancelAppliedScenarioRequestSchema>;

export const ListAppliedScenariosQuerySchema = z.object({
  state: AppliedScenarioStateSchema.optional(),
});

/** O ESCOPO CONGELADO — `plannedQty` é a cópia do ato, não a quantidade viva da ordem. */
export const AppliedScenarioOrderSchema = z.object({
  productionOrderId: z.string().uuid(),
  code: z.string().nullable(),
  plannedQty: z.number().int(),
  position: z.number().int(),
});
export type AppliedScenarioOrderView = z.infer<typeof AppliedScenarioOrderSchema>;

export const SettlementMetricSchema = z.object({
  metric: AppliedScenarioMetricKeySchema,
  predicted: z.number().finite().nullable(),
  realized: z.number().finite().nullable(),
  baseline: z.number().finite().nullable(),
  // DERIVADOS na leitura. Nulos quando qualquer operando é nulo: "—", nunca `0`.
  deltaVsPredicted: z.number().finite().nullable(),
  deltaVsBaseline: z.number().finite().nullable(),
  withinTolerance: z.boolean().nullable(),
});
export type SettlementMetric = z.infer<typeof SettlementMetricSchema>;

/**
 * A INTERFERÊNCIA EXTERNA — ordens que produziram na linha dentro da vigência e não estão no
 * escopo congelado. `detected` viaja SEMPRE: ausência de aviso não pode ser confundida
 * com ausência de verificação.
 */
export const ExternalInterferenceSchema = z.object({
  detected: z.boolean(),
  orders: z.array(
    z.object({
      productionOrderId: z.string().uuid(),
      code: z.string().nullable(),
      producedQty: z.number().finite(),
    })
  ),
});
export type ExternalInterference = z.infer<typeof ExternalInterferenceSchema>;

export const AppliedScenarioSettlementSchema = z.object({
  settledAt: z.string(),
  metrics: z.array(SettlementMetricSchema),
  /**
   * `null` = esta leitura NÃO consultou a interferência (é o caso da LISTA, que devolve dezenas de
   * compromissos e não vai varrer a produção de cada janela). Devolver `{detected:false}` ali seria
   * exatamente a confusão que a regra proíbe: ausência de aviso lida como ausência de intrusa.
   * O detalhe e a resposta da apuração sempre a trazem preenchida.
   */
  externalInterference: ExternalInterferenceSchema.nullable(),
});
export type AppliedScenarioSettlement = z.infer<typeof AppliedScenarioSettlementSchema>;

export const AppliedScenarioResponseSchema = z.object({
  id: z.string().uuid(),
  lineId: z.string().uuid(),
  lineName: z.string(),
  batchId: z.string().uuid(),
  winnerSimulationId: z.string().uuid(),
  baselineSimulationId: z.string().uuid(),
  windowStart: z.string(),
  windowEnd: z.string(),
  windowBasis: AppliedScenarioWindowBasisSchema,
  plannedMinutes: z.number().int(),
  appliedBy: z.string().uuid(),
  appliedByName: z.string().nullable(),
  reason: z.string(),
  tolerances: z.array(MetricToleranceSchema),
  engineVersion: z.string(),
  state: AppliedScenarioStateSchema,
  phase: AppliedScenarioPhaseSchema,
  /** Instante em que a apuração libera (fim + carência). Só para fases ainda apuráveis. */
  settlementAvailableAt: z.string().nullable(),
  // Princípio II / research R9 — a confiança das DUAS execuções, lado a lado. O contrafactual sem
  // confiança convida a ler um ganho de 2 pp sobre uma base INSUFFICIENT como se fosse ganho.
  //
  // Spec 079 — o nível aqui é o EXIBIDO: `displayedConfidenceLevel` da execução, caindo em
  // `confidenceLevel` (a procedência) quando ela é anterior a esta feature. Os dois campos de base
  // ao lado dizem QUAL das duas produziu o número, porque as duas pedem ações opostas — calibrar o
  // modelo, ou melhorar o cadastro do dado.
  winnerConfidence: ConfidenceLevelSchema.nullable(),
  baselineConfidence: ConfidenceLevelSchema.nullable(),
  winnerConfidenceBasis: ConfidenceBasisSchema.nullable().default(null),
  winnerTrackRecordSource: TrackRecordSourceSchema.nullable().default(null),
  baselineConfidenceBasis: ConfidenceBasisSchema.nullable().default(null),
  baselineTrackRecordSource: TrackRecordSourceSchema.nullable().default(null),
  supersededById: z.string().uuid().nullable(),
  supersededAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  settledAt: z.string().nullable(),
  settledBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  orders: z.array(AppliedScenarioOrderSchema),
  settlement: AppliedScenarioSettlementSchema.nullable(),
  /** O limiar DECLARADO no ato — viaja sempre, porque é o alvo contra o qual se lê o índice. */
  adherenceThresholdPct: z.number(),
  /** `null` = nunca avaliado (compromisso que ainda não começou, ou aderência ainda não medida). */
  adherence: AdherenceSchema.nullable(),
  /**
   * `null` enquanto não apurado. DERIVADO de `índice ≠ null ∧ índice ≥ limiar`, ambos
   * congelados — nunca coluna: guardá-lo criaria um terceiro campo capaz de contradizer os dois de
   * onde ele sai, e no dia em que contradissesse ninguém saberia qual acreditar.
   */
  countsTowardAccuracy: z.boolean().nullable(),
  /**
   * Spec 077 — o JULGAMENTO de por que divergiu, com a proposta e a trava de causas já resolvidas
   * no servidor. `null` enquanto o compromisso não está `SETTLED` (FR-048): antes de apurar não há
   * o que classificar. Custo de leitura ZERO — a derivação é aritmética sobre fatos congelados que
   * esta mesma resposta já carrega (R1), não query nova.
   */
  classification: ClassificationViewSchema.nullable(),
});
export type AppliedScenarioResponse = z.infer<typeof AppliedScenarioResponseSchema>;

export const AppliedScenarioListSchema = z.array(AppliedScenarioResponseSchema);

// Envelopes `{data,error}` das quatro rotas — o `schema.response` completo é guardrail #6.
export const AppliedScenarioEnvelopeSchema = z.object({
  data: AppliedScenarioResponseSchema,
  error: z.null(),
});
export const AppliedScenarioListEnvelopeSchema = z.object({
  data: AppliedScenarioListSchema,
  error: z.null(),
});
// A apuração NÃO tem envelope próprio: `POST /settlement` devolve o compromisso INTEIRO, cujo
// campo `settlement` é exatamente o payload que `contracts/http-endpoints.md` descreve. Uma forma
// só para as quatro rotas evita que a tela tenha de saber qual delas devolve o quê.
